import { Component, Input } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
  AlertController,
  Events,
  ModalController,
  Platform,
  PopoverController,
} from "ionic-angular";
import { AssessmentDataService } from "../../../../providers/asmt-data-service/asmt-data-service";
import { PainSiteService } from "../pain-site-card/pain-site.service";
import { NewWoundSitePopoverPage } from "./new-wound-site-popover/new-wound-site-popover";
import { NewWoundData, PreviousWound } from "./new-wound-site-service";
import { NewWoundService } from "./new-wound.service";

@Component({
  selector: "new-wound-site-card",
  templateUrl: "new-wound-site-card.html",
})
export class NewWoundSiteCardComponent {
  @Input() painScreeningData: any;
  @Input() assessmentId: string;
  @Input() sectionId: string;
  @Input() config;
  @Input() disabled: boolean = false;
  id: string;
  label: string;
  access: string;
  declinable: boolean;
  initialized: boolean = false;
  painService: PainSiteService;
  cardData: any;
  readonly: any;

  constructor(
    private newWoundService: NewWoundService,
    public modalCtrl: ModalController,
    public dataService: AssessmentDataService,
    public translatePipe: TranslateService,
    private popover: PopoverController,
    private alertCtrl: AlertController,
    private event: Events,
    private platform: Platform
  ) {}

  ngOnInit(): void {
    this.id = this.config.id;
    this.label = this.config.cardName;
    this.access = this.config.access;
    this.config["backgrounds"] =
      this.config.tabs[0].locationOfWound[1].backgrounds;
    this.painService = new PainSiteService(this.config, this.translatePipe);
    this.newWoundService.setPainService(this.painService);
    this.declinable = this.config.declinable;
    this.readonly = this.access === "read" ? true : false;
    this.event.subscribe("getTabIndex", (data) => {
      if (this.access === "write") {
        if (data === 1 || data === 2 || data === 3) {
          this.readonly = true;
        } else if (data === 0) {
          this.readonly = false;
        }
      }
    });
  }

  @Input() set data(incomingData: any) {
    if (incomingData && incomingData.data) {
      let cardData = incomingData.data;
      if (cardData) {
        this.painService.clearAllPoints();
        this.cardData = cardData
          .map((wound) => ({ ...wound.value, recordId: wound.id }))
          .sort((a, b) => a.locationIndex - b.locationIndex);
        for (let data of this.cardData) {
          if (data.canvasData) {
            data["x"] = data.canvasData.x;
            data["y"] = data.canvasData.y;
          }
          if (data.stepOneSidebarData) {
            data["category"] = data.stepOneSidebarData.locationType;
            data["name"] = data.stepOneSidebarData.locationTitle;
            // Following properties are computed in new-wound-sidebar based on current wound type
            delete data.stepOneSidebarData.originalWoundType;
            delete data.stepOneSidebarData.migrated;
          }
          this.painService.addPointData(data);
        }
      }
    }
  }

  ngOnDestroy() {
    this.newWoundService.setAllDataObservable([]);
  }

  showModal(event) {
    let allPointsData = this.newWoundService.allDataObservable;
    const woundModal = this.popover.create(
      NewWoundSitePopoverPage,
      {
        allPointsData,
      },
      {
        cssClass: "visit-print-popover",
      }
    );
    woundModal.onDidDismiss((data) => {
      if (data) {
        let allPointsData = this.newWoundService.allDataObservable.value;
        if (
          allPointsData.filter((x) => x.woundStatus === "reassess").length > 0
        ) {
          this.looseDataPopUp("looseDataMessage", data);
        } else {
          this.reassessWoundPopUp("reassessWound", data);
        }
      }
    });
    woundModal.present({
      ev: event,
    });
  }

  reassessWoundPopUp(msg: string, clickedPoint) {
    let alert = this.alertCtrl.create({
      title: this.translatePipe.instant(msg),
      buttons: [
        {
          text: this.translatePipe.instant("cancel"),
          cssClass: this.platform.is("ios")
            ? "alert-greenbtn"
            : "alert-paddingright100 alert-greenbtn text-captilize",
        },
        {
          text: this.translatePipe.instant("proceed"),
          cssClass: "text-captilize",
          handler: () => {
            const index = this.painService["dataSet"].length - 1;
            let woundClicked = this.newWoundService.getPointDataByIndex(index);
            if (
              !woundClicked ||
              !woundClicked.woundStatus ||
              woundClicked.woundStatus === "inprogress"
            ) {
              this.painService.removePoint(index);
              if (woundClicked) {
                this.newWoundService.removeLastPoint();
              }
            }
            woundClicked = this.newWoundService.getPointDataByIndex(
              clickedPoint.locationIndex
            );
            const prevWound: PreviousWound = {
              index: clickedPoint.locationIndex,
              status: woundClicked.woundStatus,
            };
            this.newWoundService.setPreviousWoundStatus(prevWound);
            woundClicked.woundStatus = "reassess";
            woundClicked.isReassessed = true;
            this.clearUnrequiredFields(woundClicked);
            this.newWoundService.setCurrentPointData(woundClicked);
            this.newWoundService.updateOrPushCurrentPoint(woundClicked);
            this.event.publish("defaultTab", true);
          },
        },
      ],
    });
    alert.present();
  }

  clearUnrequiredFields(woundClicked: NewWoundData) {
    woundClicked.stepOneSidebarData.width = "";
    woundClicked.stepOneSidebarData.length = "";
    woundClicked.stepOneSidebarData.depth = "";
    woundClicked.stepOneSidebarData.tracking = false;
    woundClicked.stepOneSidebarData.trackingOther = "";
    woundClicked.stepOneSidebarData.undermining = false;
    woundClicked.stepOneSidebarData.underminingOther = "";
    woundClicked.stepOneSidebarData.stage = "";
    woundClicked.stepOneSidebarData.exudateLevel = "";
    woundClicked.stepOneSidebarData.exudateLevelOther = "";
    woundClicked.stepOneSidebarData.consistency = "";
    woundClicked.stepOneSidebarData.consistencyOther = "";
    woundClicked.stepOneSidebarData.drainageType = "";
    woundClicked.stepOneSidebarData.drainageTypeOther = "";
    woundClicked.stepOneSidebarData.unableToMeasure = false;
    woundClicked.stepOneSidebarData.toolName = "";
    woundClicked.stepOneSidebarData.severity = null;
    woundClicked.notes.additionalNotes = "";
    woundClicked.notes.woundCareTreatment = "";
    woundClicked.score.tool = "";
    woundClicked.score.value = null;
  }

  looseDataPopUp(msg: string, clickedPoint) {
    let alert = this.alertCtrl.create({
      title: this.translatePipe.instant(msg),
      buttons: [
        {
          text: "Cancel",
          cssClass: "text-captilize",
        },
        {
          text: "Continue",
          cssClass: "text-captilize",
          handler: () => {
            let woundClicked = this.newWoundService.getPointDataByIndex(
              clickedPoint.locationIndex
            );
            woundClicked.woundStatus = "reassess";
            this.clearUnrequiredFields(woundClicked);
            this.newWoundService.setCurrentPointData(woundClicked);
            this.newWoundService.updateOrPushCurrentPoint(woundClicked);
            this.event.publish("defaultTab", true);
          },
        },
      ],
    });
    alert.present();
  }
}



// WEBPACK FOOTER //
// ./src/components/configurable/cards/new-wound-site-card/new-wound-site-card.ts